import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { debounce } from 'lodash'

import { searchCreatorTags } from 'services/api/creator-tag'
import { RootState } from 'store'

type TagOption = { value: string; label: string }

const TagsSelector: React.FC<{ value: string[]; onChange: (tags: string[]) => void }> = ({
    value,
    onChange,
}) => {
    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId)

    const loadTagSuggestions = useMemo(
        () =>
            debounce((query: string, callback: (options: TagOption[]) => void) => {
                if (!agencyId || !query || query.length < 3) {
                    callback([])
                    return
                }
                searchCreatorTags(agencyId, query)
                    .then((tags) =>
                        callback(tags.map((tag) => ({ value: tag, label: tag })))
                    )
                    .catch(() => callback([]))
            }, 200),
        [agencyId]
    )

    useEffect(() => {
        return () => loadTagSuggestions.cancel()
    }, [loadTagSuggestions])

    return (
        <AsyncCreatableSelect
            inputId="creator-tags"
            isMulti
            cacheOptions
            className="tags-select"
            classNamePrefix="tags-select"
            placeholder="Search tags or type and press Enter"
            isClearable={false}
            formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            noOptionsMessage={({ inputValue }) =>
                !inputValue || inputValue.length < 3
                    ? 'Type at least 3 characters to search'
                    : 'No tags found'
            }
            loadOptions={loadTagSuggestions}
            value={value?.map((tag) => ({ value: tag, label: tag })) ?? []}
            onChange={(selected) => {
                onChange(
                    (selected as readonly TagOption[] | null)?.map((option) => option.value) ?? []
                )
            }}
        />
    )
}

export default TagsSelector
